import {
  Badge,
  Box,
  SliceSelectCard,
  Text,
  Tooltip,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { SlicePreview } from "../slicePreview"
import { type VisualSharedSlice } from "../sliceTableData"
import { AiPrompt } from "./AiPrompt"
import { BlankSlate } from "./BlankSlate"
import type { UseCreateAiSlicesResult } from "./useCreateAiSlices"

type CreateAiSlicesHeaderProps = UseCreateAiSlicesResult

export function CreateAiSlicesHeader(props: CreateAiSlicesHeaderProps) {
  const { disabled, loading, prompt, selectedSlice, setPrompt, create } = props

  const [tooltipShown, setTooltipShown] = useState(false)

  return (
    <Box flexDirection="column" gap={4}>
      <Box gap={8}>
        <Text noSelect variant="emphasized" color="grey12">
          Add a slice with AI
        </Text>
        <Tooltip
          open={tooltipShown}
          title="Prompt to slice"
          content="Marketers, auto-generate tailored content fast. Developers, fill slices with relevant placeholders quickly.
Enter a text prompt such as 'Create a timeline of Prismic milestones' and our AI will craft content to populate the most relevant slice in your library."
        >
          <Badge
            size="medium"
            title="New"
            color="purple"
            maxWidth={150}
            onMouseEnter={() => setTooltipShown(true)}
            onMouseLeave={() => setTooltipShown(false)}
          />
        </Tooltip>
      </Box>
      <AiPrompt
        badge={selectedSlice?.name}
        disabled={disabled}
        loading={loading}
        placeholder="Describe what slice you want..."
        value={prompt}
        onValueChange={setPrompt}
        onSend={create}
      />
    </Box>
  )
}

type CreateAiSlicesProps = CreateAiSlicesHeaderProps & {
  onSelectSlice: (selection: {
    slice: VisualSharedSlice
    promptId: string
  }) => void
}

export function CreateAiSlices(props: CreateAiSlicesProps) {
  const { onSelectSlice, ...createAiSlices } = props

  if (createAiSlices.status === "idle") return null

  if (createAiSlices.status !== "done")
    return <ProcessFeedback {...createAiSlices} />

  const columns = createAiSlices.slices.reduce(
    (columns, slice, index) => {
      columns[index % 2]?.push(slice)
      return columns
    },
    [[], []] as [VisualSharedSlice[], VisualSharedSlice[]],
  )

  return (
    <Box flexDirection="column" gap={16}>
      <Text noSelect variant="h3" color="grey12">
        Choose from the generated slices
      </Text>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16}>
        {columns.map((column, index) => (
          <Box flexDirection="column" key={index} gap={16}>
            {column.map((slice) => (
              <SliceSelectCard
                key={slice.key}
                fixedHeight={false}
                label={slice.config.name}
                variation={slice.variation.name}
                onClick={() =>
                  onSelectSlice({
                    slice,
                    promptId: createAiSlices.promptId,
                  })
                }
              >
                <SlicePreview slice={slice} />
              </SliceSelectCard>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

type ProcessFeedbackProps = CreateAiSlicesHeaderProps & {
  // Refine status as it must not be "idle" or "done" here.
  status: "slicing" | "filling" | "error"
}

function ProcessFeedback(props: ProcessFeedbackProps) {
  if (props.status === "slicing")
    return (
      <BlankSlate
        type="waiting"
        title="Finding the perfect slice match"
        cancel={props.clear}
      />
    )
  else if (props.status === "filling")
    return (
      <BlankSlate
        type="waiting"
        title={`Generating "${props.slice.name}" slices`}
        cancel={props.clear}
      />
    )
  else {
    if (props.error === "simulator")
      return <BlankSlate type="simulatorMissing" />

    return <BlankSlate type="error" retry={props.create} />
  }
}
